/**
 * Loads pre-contract PPA rate guidance from Sandbox Pricing Calculator.
 */

/** Solar TPO escalator row from Sandbox Pricing IO. */
export type PreContractSandboxPpaOffer = {
    id: string;
    partner: string;
    product: string;
    offerType: string;
    financeMethod: string;
    term: number | null;
    escalator: number;
    startingRate: number;
    rateCap: number | null;
    isDefaultEscalator: boolean;
};

export type PreContractSandboxPpaRates = {
    offers: PreContractSandboxPpaOffer[];
    defaultOffer: PreContractSandboxPpaOffer | null;
    minRate: number | null;
};

const PREFERRED_ESCALATOR_PERCENTS = [2.99, 2.9] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : value == null ? [] : [value];
}

function positiveNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function finiteNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function nearlyEqual(a: number, b: number, eps = 1e-4): boolean {
    return Math.abs(a - b) <= eps;
}

/** Workbook rows mix `2.99` percent and `0.0299` decimal. */
export function sandboxEscalatorPercent(escalator: number): number {
    return escalator > 1 ? escalator : escalator * 100;
}

export function formatSandboxEscalator(escalator: number): string {
    const pct = sandboxEscalatorPercent(escalator);
    const rounded = Math.round(pct * 100) / 100;
    return `${rounded}%`;
}

/** Escalator fraction, normalized from either workbook form (`2.99` or `0.0299`). */
export function sandboxEscalatorFraction(escalator: number): number {
    const fraction = escalator > 1 ? escalator / 100 : escalator;
    return Math.round(fraction * 1e6) / 1e6;
}

/** Escalators offered on the utility form, highest first. */
export const PRECONTRACT_ESCALATOR_OPTIONS = [0.0299, 0.0199, 0] as const;

export const PRECONTRACT_DEFAULT_ESCALATOR = 0.0299;

/** Stored escalator (fraction string) back to a number, falling back to the default. */
export function resolvePreContractEscalator(value: string | number | null | undefined): number {
    if (typeof value !== 'number' && !String(value ?? '').trim()) {
        return PRECONTRACT_DEFAULT_ESCALATOR;
    }
    const parsed = typeof value === 'number' ? value : Number(String(value).replace('%', '').trim());
    if (!Number.isFinite(parsed) || parsed < 0) return PRECONTRACT_DEFAULT_ESCALATOR;
    return sandboxEscalatorFraction(parsed);
}

export function sandboxPpaOfferId(offer: Omit<PreContractSandboxPpaOffer, 'id'>): string {
    return [
        offer.partner,
        offer.financeMethod,
        offer.escalator,
        offer.startingRate,
        offer.rateCap ?? '',
    ].join('|');
}

function isTruthyFlag(value: unknown): boolean {
    return value === true || value === 1 || String(value ?? '').toLowerCase() === 'true';
}

function isSolarProductKey(key: string): boolean {
    const normalized = key.trim().toLowerCase();
    return normalized === 'solar' || normalized.startsWith('solar');
}

function partnerRank(partner: string): number {
    const normalized = partner.trim().toLowerCase().replace(/\s+/g, '');
    if (normalized === 'enfin' || normalized === 'energyfinance') return 0;
    if (normalized.includes('lightreach') || normalized.includes('sunnova')) return 1;
    return 2;
}

function collectEscalatorOffers(
    partner: string,
    product: string,
    tpoOffer: Record<string, unknown>,
    out: PreContractSandboxPpaOffer[],
): void {
    const offerType = String(tpoOffer.offerType ?? tpoOffer.OfferType ?? '').trim();
    const financeMethod = String(
        tpoOffer.financeMethod ?? tpoOffer.FinanceMethod ?? offerType,
    ).trim();
    const rows = asArray(tpoOffer.escalatorOptions ?? tpoOffer.EscalatorOptions);

    rows.forEach((row) => {
        const option = asRecord(row);
        if (!option) return;
        const startingRate = positiveNumber(option.startingRate ?? option.StartingRate);
        const escalator = finiteNumber(option.escalator ?? option.Escalator);
        if (startingRate == null || escalator == null) return;

        const offer: Omit<PreContractSandboxPpaOffer, 'id'> = {
            partner,
            product,
            offerType,
            financeMethod,
            term: finiteNumber(option.term ?? option.Term),
            escalator,
            startingRate,
            rateCap: positiveNumber(option.rateCap ?? option.RateCap),
            isDefaultEscalator: isTruthyFlag(
                option.defaultEscalatorOption ?? option.DefaultEscalatorOption,
            ),
        };
        out.push({ ...offer, id: sandboxPpaOfferId(offer) });
    });
}

function collectTpoOffersFromGroup(
    partner: string,
    product: string,
    group: unknown,
    out: PreContractSandboxPpaOffer[],
): void {
    const rec = asRecord(group);
    if (!rec) return;
    asArray(rec.tpoOffers ?? rec.TpoOffers).forEach((item) => {
        const tpo = asRecord(item);
        if (tpo) collectEscalatorOffers(partner, product, tpo, out);
    });
}

function collectSolarFromPartnerBucket(
    partner: string,
    bucket: unknown,
    out: PreContractSandboxPpaOffer[],
): void {
    if (Array.isArray(bucket)) {
        bucket.forEach((item) => collectSolarFromPartnerBucket(partner, item, out));
        return;
    }
    const rec = asRecord(bucket);
    if (!rec) return;

    const solarKeys = Object.keys(rec).filter(isSolarProductKey);
    if (solarKeys.length > 0) {
        solarKeys.forEach((key) => {
            asArray(rec[key]).forEach((group) => collectTpoOffersFromGroup(partner, key, group, out));
        });
        return;
    }

    collectTpoOffersFromGroup(partner, 'Solar', rec, out);
}

/** Flatten Solar TPO startingRate / rateCap rows from the workbook JSON. */
export function parseSandboxSolarPpaOffers(parsed: unknown): PreContractSandboxPpaOffer[] {
    const root = asRecord(parsed);
    const active =
        root?.activeOffersWithPricing ??
        root?.ActiveOffersWithPricing ??
        parsed;
    const offers: PreContractSandboxPpaOffer[] = [];

    if (Array.isArray(active)) {
        active.forEach((item) => {
            const rec = asRecord(item);
            if (!rec) return;
            Object.entries(rec).forEach(([partner, bucket]) => {
                collectSolarFromPartnerBucket(partner, bucket, offers);
            });
        });
    } else {
        const rec = asRecord(active);
        if (rec) {
            Object.entries(rec).forEach(([partner, bucket]) => {
                collectSolarFromPartnerBucket(partner, bucket, offers);
            });
        }
    }

    const seen = new Set<string>();
    return offers.filter((offer) => {
        if (seen.has(offer.id)) return false;
        seen.add(offer.id);
        return true;
    });
}

export function pickDefaultSandboxPpaOffer(
    offers: PreContractSandboxPpaOffer[],
): PreContractSandboxPpaOffer | null {
    if (offers.length === 0) return null;

    const byPreferredEscalator = PREFERRED_ESCALATOR_PERCENTS.map((pct) =>
        offers.filter((offer) => nearlyEqual(sandboxEscalatorPercent(offer.escalator), pct)),
    ).find((pool) => pool.length > 0);

    const pool = byPreferredEscalator ?? offers;
    const defaulted = pool.filter((offer) => offer.isDefaultEscalator);
    const ranked = (defaulted.length > 0 ? defaulted : pool).slice().sort((a, b) => {
        const partnerDelta = partnerRank(a.partner) - partnerRank(b.partner);
        if (partnerDelta !== 0) return partnerDelta;
        if (a.startingRate !== b.startingRate) return a.startingRate - b.startingRate;
        return a.partner.localeCompare(b.partner);
    });
    return ranked[0] ?? null;
}

function parseCellJson(outputValues: unknown): unknown {
    if (!Array.isArray(outputValues) || outputValues.length === 0) return null;
    const cell = outputValues[0] as Record<string, unknown> | null;
    const raw = cell?.CellValue ?? cell?.cellValue;
    if (typeof raw !== 'string' || !raw.trim()) return null;
    return JSON.parse(raw);
}

/** Request B3/C3 from the same workbook used by ContractV2Manager.GetExcelPriceRequest. */
export async function fetchPreContractSandboxPpaRates(input: {
    state: string;
    utility: string;
    batteryCount: number;
}): Promise<PreContractSandboxPpaRates | null> {
    const { default: masterServices } = await import(
        '../../services/oneButtonServices/masterServices'
    );
    const outputValues = await masterServices.cascadingDropDown({
        ExcelFileName: 'Sandbox Pricing Calculator',
        SheetName: 'Sandbox Pricing IO',
        InputValues: [
            {
                Type: 'String',
                CellNumber: 'B3',
                CellValue: JSON.stringify(input),
            },
        ],
        OutputCells: ['C3'],
    });

    console.log('Pre-contract Sandbox Pricing Excel response', outputValues);

    const parsed = parseCellJson(outputValues);
    const offers = parseSandboxSolarPpaOffers(parsed);
    const defaultOffer = pickDefaultSandboxPpaOffer(offers);
    const minRate =
        offers.length > 0 ? Math.min(...offers.map((offer) => offer.startingRate)) : null;

    console.log('Pre-contract Sandbox Pricing PPA rates', {
        input,
        defaultOffer,
        minRate,
        offers,
        parsed,
    });

    if (!defaultOffer) return null;
    return { offers, defaultOffer, minRate };
}
